import { useState } from "react";
import { cn } from "@/lib/utils";
import { 
  Menu, 
  X,
  Map, 
  ShoppingBag, 
  Users, 
  User,
  Scale,
  Backpack,
  Target,
  Crown,
  HelpCircle,
  LogOut,
  Swords,
  UserCircle,
  Book,
  Anvil,
  Trophy,
  Shield,
  BookOpen,
  Gift
} from "lucide-react";

const MENU_ITEMS = [
  { id: 'profile', icon: User, label: 'Profile' },
  { id: 'switch-profile', icon: UserCircle, label: 'Switch' },
  { id: 'inventory', icon: Backpack, label: 'Items' },
  { id: 'codex', icon: Book, label: 'Codex' },
  { id: 'crafting', icon: Anvil, label: 'Craft' },
  { id: 'map', icon: Map, label: 'Map' },
  { id: 'challenges', icon: Target, label: 'Quests' },
  { id: 'pvp', icon: Swords, label: 'PvP' },
  { id: 'exchange', icon: Scale, label: 'Trade' },
  { id: 'guild', icon: Shield, label: 'Guild' },
  { id: 'social', icon: Users, label: 'Social' },
  { id: 'shop', icon: ShoppingBag, label: 'Shop' },
  { id: 'leaderboard', icon: Trophy, label: 'Ranks' },
  { id: 'daily-spin', icon: Gift, label: 'Daily' },
  { id: 'tutorial', icon: HelpCircle, label: 'Help' },
  { id: 'story', icon: BookOpen, label: 'Story' },
  { id: 'membership', icon: Crown, label: 'Premium', premium: true },
];

export function HamburgerMenu({ 
  onMenuSelect,
  hasNotification = false,
  isPremiumMember = false,
  level = 1,
  currentXP = 0,
  neededXP = 100,
  prestige = 0,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const xpPercent = neededXP > 0 ? Math.min((currentXP / neededXP) * 100, 100) : 0;

  const handleSelect = (id) => {
    onMenuSelect?.(id);
    setIsOpen(false);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-[2px]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Menu Panel */}
      <div className={cn(
        "fixed left-0 bottom-0 z-50 transition-transform duration-300 ease-out",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="bg-slate-900/95 backdrop-blur-xl rounded-tr-2xl border-t border-r border-white/10 overflow-hidden mb-16">
          {/* 3×6 Icon Grid (scrollable) */}
          <div className="grid grid-cols-3 gap-1 p-3 max-h-[320px] overflow-y-auto" data-testid="menu-grid">
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              const isPremiumItem = item.premium;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item.id)}
                  data-testid={`menu-${item.id}`}
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-1 w-[72px] h-[64px] rounded-xl",
                    "transition-all duration-150 active:scale-90",
                    isPremiumItem
                      ? "bg-gradient-to-b from-amber-500/15 to-orange-500/10 border border-amber-500/25 hover:from-amber-500/25 hover:to-orange-500/20"
                      : "hover:bg-white/10 active:bg-white/15"
                  )}
                >
                  <div className="relative">
                    <Icon className={cn(
                      "w-5 h-5",
                      isPremiumItem ? "text-amber-400" : "text-slate-200"
                    )} />
                    {item.id === 'challenges' && hasNotification && (
                      <span className="absolute -top-1 -right-1.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                    {isPremiumItem && isPremiumMember && (
                      <span className="absolute -top-1 -right-2 w-2 h-2 bg-amber-400 rounded-full" />
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] font-medium leading-none",
                    isPremiumItem ? "text-amber-300/80" : "text-slate-400"
                  )}>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Level & XP bar */}
          <div className="px-3 pb-2" data-testid="menu-level-xp">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-white whitespace-nowrap">
                {prestige > 0 ? `P${prestige} ` : ''}Lv.{level}
              </span>
              <div className="flex-1 h-1.5 bg-slate-700/60 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all duration-500"
                  style={{ width: `${xpPercent}%` }}
                />
              </div>
              <span className="text-[9px] text-slate-500 tabular-nums whitespace-nowrap">{currentXP}/{neededXP}</span>
            </div>
          </div>

          {/* Logout */}
          <div className="px-3 pb-3">
            <button
              onClick={() => handleSelect('logout')}
              data-testid="menu-logout"
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors border border-red-500/15 text-xs font-medium"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed left-4 bottom-4 z-50",
          "w-14 h-14 rounded-full",
          "flex items-center justify-center",
          "shadow-lg transition-all duration-200",
          "active:scale-95",
          "bg-slate-800 hover:bg-slate-700 border border-white/10"
        )}
        data-testid="hamburger-menu-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Menu className="w-6 h-6 text-white" />
        )}
        
        {!isOpen && hasNotification && (
          <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-slate-800 animate-pulse" />
        )}
      </button>
    </>
  );
}

export default HamburgerMenu;
